import { toast } from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import inventoryApi from '../../../services/inventoryApi';
import adminAPI from '../../../services/adminApi';
import CustomSelect from '../../Common/CustomSelect';
import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import SupplierPaymentForm from './SupplierPaymentForm';
import SupplierPaymentView from './SupplierPaymentView';
import { openSupplierPaymentReport } from '../../../utils/openInventoryReport';
import { canPrintSupplierPayment } from '../../../utils/documentPrintGate';
import ChequePrintAction, { supplierPaymentChequeLines } from '../../Common/ChequePrintAction';
import { DoubleEntryModal, DoubleEntryToggleButton } from '../../Finance/DoubleEntries/DoubleEntryPanel';

const SupplierPaymentManagement = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // Data state
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [companyInfo, setCompanyInfo] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setCompanyInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[SupplierPaymentManagement] could not load location info:', e.message);
      }
    })();
  }, []);

  const handlePrintPayment = async (payment) => {
    await openSupplierPaymentReport(payment, companyInfo);
  };
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Reference data
  const [suppliers, setSuppliers] = useState([]);

  // UI state
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'table' or 'cards'
  const [showForm, setShowForm] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingPaymentId, setViewingPaymentId] = useState(null);
  const [doubleEntryPayment, setDoubleEntryPayment] = useState(null); // payment whose GL double entries are shown
  const toggleDoubleEntries = (payment) => setDoubleEntryPayment(prev => (prev?._id === payment._id ? null : payment));

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending Approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'paid', label: 'Paid' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    fetchPayments();
    fetchSuppliers();
  }, [currentPage, statusFilter, supplierFilter, paymentMethodFilter, searchTerm, dateRange]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        supplier: supplierFilter !== 'all' ? supplierFilter : undefined,
        paymentMethod: paymentMethodFilter !== 'all' ? paymentMethodFilter : undefined,
        startDate: dateRange.start || undefined,
        endDate: dateRange.end || undefined
      };

      const response = await inventoryApi.getSupplierPayments({ ...params, sort: 'createdAt', order: 'desc' });

      setPayments(response.data || []);
      setTotalItems(response.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching supplier payments:', err);
      console.error('Error details:', err.response?.data);
      toast.error(err?.response?.data?.message || 'Failed to fetch supplier payments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await inventoryApi.getSuppliers({ limit: 1000 });
      setSuppliers(response.data || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filter, value) => {
    switch (filter) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'supplier':
        setSupplierFilter(value);
        break;
      case 'paymentMethod':
        setPaymentMethodFilter(value);
        break;
    }
    setCurrentPage(1);
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSupplierFilter('all');
    setPaymentMethodFilter('all');
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
  };

  const handleCreatePayment = () => {
    // Check permission before opening modal
    if (!hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:CREATE', 'INVENTORY:ADMIN'])) {
      toast.error('You do not have permission to create supplier payments');
      return;
    }
    setEditingPaymentId(null);
    setShowForm(true);
  };

  const handleEditPayment = (payment) => {
    // Check permission before opening modal
    if (!hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:UPDATE', 'INVENTORY:ADMIN'])) {
      toast.error('You do not have permission to edit supplier payments');
      return;
    }
    setEditingPaymentId(payment._id);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingPaymentId(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingPaymentId(null);
    fetchPayments();
  };

  const handleViewPayment = (payment) => {
    setViewingPaymentId(payment._id);
    setShowViewModal(true);
  };

  // Popup closed: reload — it may have approved / rejected / processed the payment
  const handleViewClose = () => {
    setShowViewModal(false);
    setViewingPaymentId(null);
    fetchPayments();
  };

  const handleDeletePayment = async (payment) => {
    // Check permission before delete action
    if (!hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:DELETE', 'INVENTORY:ADMIN'])) {
      toast.error('You do not have permission to delete supplier payments');
      return;
    }

    try {
      await inventoryApi.deleteSupplierPayment(payment._id);
      toast.success('Payment deleted successfully');
      fetchPayments();
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete payment. Please try again.');
    }
  };

  const handleApprovePayment = async (payment) => {
    // Check permission before approval action
    if (!hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:APPROVE', 'INVENTORY:ADMIN'])) {
      toast.error(error?.response?.data?.message || 'You do not have permission to approve supplier payments');
      return;
    }

    try {
      await inventoryApi.approveSupplierPayment(payment._id, { comments: 'Approved via management interface' });
      toast.success('Payment approved successfully');
      fetchPayments();
    } catch (error) {
      console.error('Error approving payment:', error);
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to approve payment. Please try again.');
    }
  };

  const handleRejectPayment = async (payment) => {
    // Check permission before reject action
    if (!hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:APPROVE', 'INVENTORY:ADMIN'])) {
      toast.error(error?.response?.data?.message || 'You do not have permission to reject supplier payments');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this payment? It will be returned to draft status.')) {
      return;
    }

    try {
      await inventoryApi.rejectSupplierPayment(payment._id, { comments: 'Rejected via management interface' });
      toast.success('Payment rejected successfully');
      fetchPayments();
    } catch (error) {
      console.error('Error rejecting payment:', error);
      toast.error(error?.response?.data?.message || 'Failed to reject payment. Please try again.');
    }
  };

  const handleProcessPayment = async (payment) => {
    // Check permission before process action
    if (!hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:PROCESS', 'INVENTORY:ADMIN'])) {
      toast.error(error?.response?.data?.message || 'You do not have permission to process supplier payments');
      return;
    }

    try {
      await inventoryApi.processSupplierPayment(payment._id, {});
      toast.success('Payment processing initiated successfully');
      fetchPayments();
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error(error?.response?.data?.message || 'Failed to process payment. Please try again.');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const statusColors = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      paid: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash':
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'bank_transfer':
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M10.5 3L12 2l1.5 1H21l-3 6H6l-3-6h7.5z" />
          </svg>
        );
      case 'cheque':
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'credit_card':
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (loading && payments.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-white shadow-sm border border-gray-200">
      {/* Error Display */}
      {error && (
        <div className="mx-4 mt-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
          <div className="flex items-center">
            <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-500"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="mx-4 mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded p-3">
          <div className="flex items-center">
            <svg className="h-4 w-4 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium text-green-800">{success}</p>
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto text-green-600 hover:text-green-500"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          {/* Search */}
          <div className="flex-1">
            <TextInput
              variant="input"
              type="text"
              placeholder="Search by voucher number, supplier, notes..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              icon={({ className }) => (
                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
              className="text-sm py-1.5 border border-gray-300 shadow-sm !rounded-sm"
            />
          </div>

          {/* Filters and Actions */}
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <CustomSelect
              value={statusFilter}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              options={statusOptions}
              size="sm"
              variant="default"
              className="min-w-[120px] !rounded-sm"
            />

            <CustomSelect
              value={supplierFilter}
              onChange={(e) => handleFilterChange('supplier', e.target.value)}
              options={[
                { value: 'all', label: 'All Suppliers' },
                ...suppliers.map(supplier => ({
                  value: supplier._id,
                  label: supplier.name
                }))
              ]}
              size="sm"
              variant="default"
              className="min-w-[140px] !rounded-sm"
            />

            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
              <button
                onClick={() => setViewMode('table')}
                className={`px-2 py-1 text-xs font-medium rounded transition-all ${viewMode === 'table'
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                title="List view"
              >
                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                List
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-2 py-1 text-xs font-medium rounded transition-all ${viewMode === 'cards'
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                title="Card view"
              >
                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Cards
              </button>
            </div>

            {/* Add Payment Button */}
            {hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:CREATE', 'INVENTORY:ADMIN']) && (
              <button
                onClick={handleCreatePayment}
                className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
              >
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Payment
              </button>
            )}
          </div>
        </div>

        {/* Date Range Filters */}
        <div className="mt-2 flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Date Range:</span>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => handleDateRangeChange('start', e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => handleDateRangeChange('end', e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      {/* Pagination Section */}
        <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-b border-gray-200 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Filter Summary */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              {searchTerm && (
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs">
                  Search: "{searchTerm}"
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs">
                  Status: {statusOptions.find(opt => opt.value === statusFilter)?.label}
                </span>
              )}
              {supplierFilter !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs">
                  Supplier: {suppliers.find(s => s._id === supplierFilter)?.name || 'Selected'}
                </span>
              )}
              {(dateRange.start || dateRange.end) && (
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-800 text-xs">
                  Date Range
                </span>
              )}
              {(searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || dateRange.start || dateRange.end) && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  title="Reset filters"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Clear
                </button>
              )}
            </div>

            {/* Results Info */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="text-sm flex items-center gap-3">
                <div>
                  <span className="font-semibold text-gray-900">
                    {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalItems)}
                  </span>
                  <span className="text-gray-500 mx-1">of</span>
                  <span className="font-semibold text-gray-900">{totalItems}</span>
                  <span className="text-gray-500 ml-1">payments</span>
                  {totalPages > 1 && (
                    <span className="text-xs text-gray-500 ml-2">
                      (Page {currentPage} of {totalPages})
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500">
                    {(searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || dateRange.start || dateRange.end) ? 'Filtered results' : 'All payments'}
                  </p>
                </div>
                {loading && payments.length > 0 && (
                  <div className="flex items-center">
                    <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center gap-3">
              {/* Items per page */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 font-medium">Show:</span>
                <CustomSelect
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  disabled={loading}
                  options={[
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' }
                  ]}
                  size="sm"
                  variant="minimal"
                  className="min-w-[70px]"
                />
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage <= 1 || loading}
                  className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  title="First page"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1 || loading}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="hidden sm:inline">Previous</span>
                </button>

                {/* Page Numbers */}
                <div className="hidden md:flex items-center gap-1">
                  {(() => {
                    const getPageNumbers = () => {
                      const pages = [];
                      const maxVisible = 7;
                      const halfVisible = Math.floor(maxVisible / 2);

                      if (totalPages <= maxVisible) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);

                        let start = Math.max(2, currentPage - halfVisible);
                        let end = Math.min(totalPages - 1, currentPage + halfVisible);

                        if (currentPage <= halfVisible + 1) {
                          end = Math.min(totalPages - 1, maxVisible - 1);
                        }

                        if (currentPage >= totalPages - halfVisible) {
                          start = Math.max(2, totalPages - maxVisible + 2);
                        }

                        if (start > 2) {
                          pages.push('...');
                        }

                        for (let i = start; i <= end; i++) {
                          pages.push(i);
                        }

                        if (end < totalPages - 1) {
                          pages.push('...');
                        }

                        if (totalPages > 1) {
                          pages.push(totalPages);
                        }
                      }

                      return pages;
                    };

                    return getPageNumbers().map((pageNum, index) => {
                      if (pageNum === '...') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-400 text-sm">
                            ...
                          </span>
                        );
                      }

                      const isCurrentPage = pageNum === currentPage;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          disabled={loading}
                          className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${isCurrentPage
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    });
                  })()}
                </div>

                {/* Mobile page indicator */}
                <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                  <span className="text-sm font-medium text-gray-900">{currentPage}</span>
                  <span className="text-sm text-gray-500 mx-1">of</span>
                  <span className="text-sm font-medium text-gray-900">{totalPages}</span>
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages || loading}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                >
                  <span className="hidden sm:inline">Next</span>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage >= totalPages || loading}
                  className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  title="Last page"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        {viewMode === 'cards' ? (
          <div className="px-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {payments.length === 0 ? (
                <div className="col-span-full bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">No supplier payments found</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {(searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || paymentMethodFilter !== 'all' || dateRange.start || dateRange.end) ?
                      'Try adjusting your search or filter criteria.' :
                      'Get started by creating your first supplier payment.'
                    }
                  </p>
                  {!searchTerm && statusFilter === 'all' && supplierFilter === 'all' && !dateRange.start && !dateRange.end && (
                    <button
                      onClick={handleCreatePayment}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                      style={{ display: hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:CREATE', 'INVENTORY:ADMIN']) ? 'inline-flex' : 'none' }}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create Payment
                    </button>
                  )}
                </div>
              ) : (
                payments.map((payment) => (
                  <div
                    key={payment._id}
                    className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-40 w-full"
                  >
                    <div className="p-3 h-full flex flex-col">
                      {/* Card Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <DoubleEntryToggleButton className="flex-shrink-0" open={doubleEntryPayment?._id === payment._id} onClick={() => toggleDoubleEntries(payment)} />
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                            {payment.paymentVoucherNo?.substring(0, 2)?.toUpperCase() || 'SP'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors cursor-pointer" onClick={() => handleViewPayment(payment)}>
                              {payment.paymentVoucherNo}
                            </h3>
                            <p className="text-xs text-gray-500 truncate">{formatDate(payment.paymentDate)}</p>
                          </div>
                        </div>

                        {/* Icon action buttons — hidden until hover */}
                        <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <button
                            onClick={() => handleViewPayment(payment)}
                            className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                            title="View payment"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>

                          <ChequePrintAction payment={payment} sourceType="SupplierPayment" lines={supplierPaymentChequeLines(payment)} payee={payment.supplier?.name || ''} />
                          {canPrintSupplierPayment(payment.status) && (
                            <button
                              onClick={() => handlePrintPayment(payment)}
                              className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                              title="Print PDF"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                              </svg>
                            </button>
                          )}

                          {payment.status === 'draft' && hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:UPDATE', 'INVENTORY:ADMIN']) && (
                            <button
                              onClick={() => handleEditPayment(payment)}
                              className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                              title="Edit payment"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}

                          {payment.status === 'pending' && hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:APPROVE', 'INVENTORY:ADMIN']) && (
                            <>
                              <button
                                onClick={() => handleApprovePayment(payment)}
                                className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                                title="Approve payment"
                              >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleRejectPayment(payment)}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                title="Reject payment"
                              >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </>
                          )}

                          {payment.status === 'approved' && hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:PROCESS', 'INVENTORY:ADMIN']) && (
                            <button
                              onClick={() => handleProcessPayment(payment)}
                              className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-all"
                              title="Process payment"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            </button>
                          )}

                          {payment.status === 'draft' && hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:DELETE', 'INVENTORY:ADMIN']) && (
                            <button
                              onClick={() => setShowDeleteConfirm(payment)}
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                              title="Delete payment"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className="text-xs text-gray-600 mb-2 flex-1">
                        <div className="flex justify-between mb-1">
                          <span>Supplier:</span>
                          <span className="font-medium truncate ml-1">{payment.supplier?.name || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between mb-1">
                          <span>Amount:</span>
                          <span className="font-medium text-green-600">{parseFloat(payment.summary?.netPaymentAmount || 0).toFixed(2)}</span>
                        </div>
                        {payment.supplier?.code && (
                          <div className="flex justify-between">
                            <span>Code:</span>
                            <span className="font-medium">{payment.supplier.code}</span>
                          </div>
                        )}
                      </div>

                      {/* Status badge */}
                      <div className="flex flex-wrap gap-1 mt-auto">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getStatusColor(payment.status)}`}>
                          {payment.status?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-max w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Voucher</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Supplier</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-12 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <h3 className="text-sm font-medium text-gray-900 mb-2">No supplier payments found</h3>
                        <p className="text-sm text-gray-500">
                          {(searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || paymentMethodFilter !== 'all' || dateRange.start || dateRange.end) ?
                            'Try adjusting your search or filter criteria.' :
                            'Get started by creating your first supplier payment.'
                          }
                        </p>
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment) => (
                      <tr key={payment._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-start gap-2">
                            <DoubleEntryToggleButton className="mt-0.5" open={doubleEntryPayment?._id === payment._id} onClick={() => toggleDoubleEntries(payment)} />
                            <div>
                              <div className="text-sm font-semibold text-gray-900 cursor-pointer hover:text-indigo-600"
                                onClick={() => handleViewPayment(payment)}>
                                {payment.paymentVoucherNo}
                              </div>
                              <p className="text-xs text-gray-500">{formatDate(payment.paymentDate)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{payment.supplier?.name || 'N/A'}</div>
                          <p className="text-xs text-gray-500">{payment.supplier?.code || ''}</p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDate(payment.paymentDate)}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{parseFloat(payment.summary?.netPaymentAmount || 0).toFixed(2)}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(payment.status)}`}>
                            {payment.status?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-1">
                            <button
                              onClick={() => handleViewPayment(payment)}
                              className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                              title="View details"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <ChequePrintAction payment={payment} sourceType="SupplierPayment" lines={supplierPaymentChequeLines(payment)} payee={payment.supplier?.name || ''} />
                            {canPrintSupplierPayment(payment.status) && (
                              <button
                                onClick={() => handlePrintPayment(payment)}
                                className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                                title="Print PDF"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                              </button>
                            )}
                            {payment.status === 'draft' && (
                              <>
                                {hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:UPDATE', 'INVENTORY:ADMIN']) && (
                                  <button
                                    onClick={() => handleEditPayment(payment)}
                                    className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                                    title="Edit payment"
                                  >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                )}
                                {hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:DELETE', 'INVENTORY:ADMIN']) && (
                                  <button
                                    onClick={() => setShowDeleteConfirm(payment)}
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                    title="Delete payment"
                                  >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </>
                            )}
                            {payment.status === 'pending' && hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:APPROVE', 'INVENTORY:ADMIN']) && (
                              <>
                                <button
                                  onClick={() => handleApprovePayment(payment)}
                                  className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                                  title="Approve payment"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleRejectPayment(payment)}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                  title="Reject payment"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </>
                            )}
                            {payment.status === 'approved' && hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:PROCESS', 'INVENTORY:ADMIN']) && (
                              <button
                                onClick={() => handleProcessPayment(payment)}
                                className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-all"
                                title="Process payment"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* GL double entries of a payment */}
        {doubleEntryPayment && (
          <DoubleEntryModal
            documentType="SupplierPayment"
            documentId={doubleEntryPayment._id}
            title={doubleEntryPayment.paymentVoucherNo}
            onClose={() => setDoubleEntryPayment(null)}
          />
        )}

        {/* Supplier Payment View Modal */}
        {showViewModal && (
          <SupplierPaymentView
            paymentId={viewingPaymentId}
            onClose={handleViewClose}
          />
        )}

        {/* Supplier Payment Form Modal */}
        {showForm && (
          <SupplierPaymentForm
            paymentId={editingPaymentId}
            onClose={handleFormClose}
            onSuccess={handleFormSuccess}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white rounded-lg shadow-lg border border-gray-100">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Delete Payment</h3>
                    <p className="text-sm text-gray-600">This action cannot be undone.</p>
                  </div>
                </div>
                <p className="text-sm text-gray-700 mb-6">
                  Are you sure you want to delete payment voucher "{showDeleteConfirm.paymentVoucherNo}"?
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  {hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:DELETE', 'INVENTORY:ADMIN']) && (
                    <button
                      onClick={() => handleDeletePayment(showDeleteConfirm)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      );
};

      export default SupplierPaymentManagement;